import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { usePlatformApi } from "../platform/PlatformApiProvider";
import type { ProfileModuleId } from "../model/access";
import type {
  AccountEntitlement,
  AccountExperienceInput,
  AccountSettings,
  ArtifactTemplateInput,
  BriefingAlert,
  Profile,
  ProfileInput,
  ProfileModule,
} from "./platform-api";

export const queryKeys = {
  account: (userId: string) => ["account", userId] as const,
  settings: (userId: string) => [...queryKeys.account(userId), "settings"] as const,
  entitlement: (userId: string) => [...queryKeys.account(userId), "entitlement"] as const,
  profiles: (userId: string) => [...queryKeys.account(userId), "profiles"] as const,
  modules: (userId: string, profileIds: string[]) =>
    [...queryKeys.account(userId), "profile-modules", [...profileIds].sort()] as const,
  briefing: (userId: string) => [...queryKeys.account(userId), "daily-briefing"] as const,
  templates: (userId: string) => [...queryKeys.account(userId), "artifact-templates"] as const,
};

export function useArtifactTemplates(userId: string) {
  const api = usePlatformApi();
  return useQuery({
    queryKey: queryKeys.templates(userId),
    queryFn: ({ signal }) => api.artifactTemplates(userId, signal),
  });
}

export function useSaveArtifactTemplate(userId: string) {
  const api = usePlatformApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ArtifactTemplateInput) => api.saveArtifactTemplate(userId, input),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.templates(userId) });
    },
  });
}

export function useDeleteArtifactTemplate(userId: string) {
  const api = usePlatformApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => api.deleteArtifactTemplate(templateId),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.templates(userId) });
    },
  });
}

export function useApplyArtifactTemplate() {
  const api = usePlatformApi();
  return useMutation({
    mutationFn: (input: { templateId: string; profileId: string }) =>
      api.applyArtifactTemplate(input.templateId, input.profileId),
    retry: false,
  });
}

export function useDailyBriefing(userId: string): UseQueryResult<BriefingAlert[]> {
  const api = usePlatformApi();
  return useQuery({
    queryKey: queryKeys.briefing(userId),
    queryFn: ({ signal }) => api.dailyBriefing(signal),
  });
}

export function useAccountSettings(userId: string): UseQueryResult<AccountSettings | null> {
  const api = usePlatformApi();
  return useQuery({
    queryKey: queryKeys.settings(userId),
    queryFn: ({ signal }) => api.accountSettings(userId, signal),
  });
}

export function useEntitlement(userId: string): UseQueryResult<AccountEntitlement | null> {
  const api = usePlatformApi();
  return useQuery({
    queryKey: queryKeys.entitlement(userId),
    queryFn: ({ signal }) => api.entitlement(userId, signal),
  });
}

export function useProfiles(userId: string): UseQueryResult<Profile[]> {
  const api = usePlatformApi();
  return useQuery({
    queryKey: queryKeys.profiles(userId),
    queryFn: ({ signal }) => api.profiles(userId, signal),
  });
}

export function useModules(userId: string, profileIds: string[]): UseQueryResult<ProfileModule[]> {
  const api = usePlatformApi();
  return useQuery({
    queryKey: queryKeys.modules(userId, profileIds),
    queryFn: ({ signal }) => api.modules(profileIds, signal),
    enabled: profileIds.length > 0,
  });
}

export function useSaveExperience(userId: string) {
  const api = usePlatformApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AccountExperienceInput & { displayName: string }) => {
      const saved = await api.saveExperience(userId, { ...input, completeOnboarding: false });
      if (input.usageMode === "personal") {
        await api.ensurePersonalProfile(userId, input.displayName);
      }
      if (input.completeOnboarding) return api.saveExperience(userId, input);
      return saved;
    },
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.account(userId) });
    },
  });
}

export function useEnsurePersonalProfile(userId: string) {
  const api = usePlatformApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (displayName: string) => api.ensurePersonalProfile(userId, displayName),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.profiles(userId) });
    },
  });
}

export function useSaveProfile(userId: string) {
  const api = usePlatformApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProfileInput) => api.saveProfile(userId, input),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.profiles(userId) });
    },
  });
}

export function useDeleteProfile(userId: string) {
  const api = usePlatformApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profileId: string) => api.deleteClientProfile(profileId),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.profiles(userId) });
    },
  });
}

export function useSaveModules(userId: string, profileIds: string[]) {
  const api = usePlatformApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { profileId: string; enabled: ProfileModuleId[] }) =>
      api.saveModules(input.profileId, input.enabled),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.modules(userId, profileIds) });
    },
  });
}
